import { Address, AddressDetails } from 'features/declaration/address/models/types'
import { parseAddressResponse, toCreateOrUpdateAddressRequest } from 'features/declaration/address/models/mapper'
import { ContactPerson } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import {
  TraderType,
  TraderTargetEnum,
  TraderTargetType, ConsignorFormValues, SellerFormValues, BuyerFormValues, ConsigneeFormValues, CarrierFormValues,
} from 'features/declaration/models/schemas/tradersSchema'
import { hasText } from 'shared/lib/common-util'
import { isTraderWithContactPerson, isTraderWithPersonType } from 'features/declaration/models/helpers'
import { ConsignmentDetailResponseV3 } from '../../../../api/models/consignment-detail-response-v3'
import { ConsignmentDetail, ConsignmentDetailDetails } from './types'
import { ConsignmentDetailUpdate } from './request'
import {
  AddressResponse,
  ConsignmentDetailRequest,
  ConsignmentDetailRequestTargetEnum, ConsignmentDetailResponse,
  CreateOrUpdateContactPersonRequest,
} from '../../../../api/models'
import { AddressResponseV3 } from '../../../../api/models/address-response-v3'

export function toConsignmentDetailUpdate(
  transitOperationId: number,
  trader: TraderType,
): ConsignmentDetailUpdate {
  return {
    transitOperationId,
    id: trader.id,
    typeOfPerson: isTraderWithPersonType(trader) && trader.typeOfPerson ? trader.typeOfPerson : null, // TODO: pass it in as argument, rather than infer it?
    name: trader.name,
    target: trader.target,
    address: trader.address,
    identificationNumber: trader.identificationNumber,
    contactPerson: isTraderWithContactPerson(trader) && trader.contactPerson ? trader.contactPerson : null,
  }
}

export function fromAddressResponse(addressResponse: AddressResponseV3): Address {
  return {
    id: addressResponse.id,
    country: addressResponse.country,
  }
}

export function fromConsignmentDetailResponse(response: ConsignmentDetailResponseV3, target: TraderTargetType): ConsignmentDetail {
  return {
    id: response.id,
    name: response.name ?? '',
    address: response.address ? fromAddressResponse(response.address) : null,
    target: response ? TraderTargetEnum.parse(response?.target) : target,
  }
}

function fromAddressDetailsResponse(addressResponse: AddressResponse | null | undefined): AddressDetails | null {
  if (!addressResponse) return null

  return {
    id: addressResponse.id,
    addressLine: addressResponse.addressLine ?? '',
    street: addressResponse.street ?? '',
    streetNumber: addressResponse.streetNumber ?? '',
    country: addressResponse.country ?? '',
    city: addressResponse.city ?? '',
    postcode: addressResponse.postcode ?? '',
  }
}

export function fromConsignmentDetailDetailedResponse(
  response: ConsignmentDetailResponse,
  target: TraderTargetType,
): ConsignmentDetailDetails {
  return {
    id: response.id,
    typeOfPerson: response?.typeOfPerson ?? '',
    address: fromAddressDetailsResponse(response?.address),
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    identificationNumber: response?.identificationNumber ?? '',
    name: response?.name ?? '',
    target: response ? TraderTargetEnum.parse(response?.target) : target,
  }
}

function toCreateOrUpdateContactPersonRequest(
  contact: ContactPerson | null,
  transitOperationId: number,
): CreateOrUpdateContactPersonRequest | null {
  if (contact === null) {
    return null
  }
  if (!hasText(contact.name) && !hasText(contact.email) && !hasText(contact.telephone)) {
    return null
  }

  return {
    id: contact.id,
    name: contact.name,
    email: contact.email,
    telephone: contact.telephone,
    transitOperationId,
  }
}

export function toConsigneeFormValues(consignmentDetail: ConsignmentDetailDetails): ConsigneeFormValues {
  return {
    id: consignmentDetail.id,
    target: 'CONSIGNEE',
    name: consignmentDetail.name,
    typeOfPerson: consignmentDetail?.typeOfPerson ?? undefined,
    deleted: false,
    identificationNumber: consignmentDetail.identificationNumber,
    address: consignmentDetail.address ?? parseAddressResponse(null),
    contactPerson: consignmentDetail.contactPerson,
  }
}

export function toConsignorFormValues(consignmentDetail: ConsignmentDetailDetails): ConsignorFormValues {
  return {
    id: consignmentDetail.id,
    target: 'CONSIGNOR',
    name: consignmentDetail.name,
    typeOfPerson: consignmentDetail?.typeOfPerson ?? undefined,
    deleted: false,
    identificationNumber: consignmentDetail.identificationNumber,
    address: consignmentDetail.address ?? parseAddressResponse(null),
    contactPerson: consignmentDetail.contactPerson,
  }
}

export function toSellerFormValues(consignmentDetail: ConsignmentDetailDetails): SellerFormValues {
  return {
    id: consignmentDetail.id,
    target: 'SELLER',
    name: consignmentDetail.name,
    typeOfPerson: consignmentDetail?.typeOfPerson ?? undefined,
    deleted: false,
    identificationNumber: consignmentDetail.identificationNumber,
    address: consignmentDetail.address ?? parseAddressResponse(null),
    contactPerson: consignmentDetail.contactPerson,
  }
}

export function toBuyerFormValues(consignmentDetail: ConsignmentDetailDetails): BuyerFormValues {
  return {
    id: consignmentDetail.id,
    target: 'BUYER',
    name: consignmentDetail.name,
    typeOfPerson: consignmentDetail?.typeOfPerson ?? undefined,
    deleted: false,
    identificationNumber: consignmentDetail.identificationNumber,
    address: consignmentDetail.address ?? parseAddressResponse(null),
    contactPerson: consignmentDetail.contactPerson,
  }
}

export function toConsignmentDetailRequest(update: ConsignmentDetailUpdate): ConsignmentDetailRequest {
  const { transitOperationId } = update

  return {
    id: update.id ?? undefined,
    transitOperationId,
    typeOfPerson: isTraderWithPersonType(update) ? update.typeOfPerson : null,
    name: update.name,
    target: ConsignmentDetailRequestTargetEnum[update.target],
    address: update.address !== null ? toCreateOrUpdateAddressRequest(update.address, transitOperationId) : null,
    contactPerson: isTraderWithContactPerson(update)
      ? toCreateOrUpdateContactPersonRequest(update.contactPerson, transitOperationId)
      : undefined,
    identificationNumber: update.identificationNumber,
  }
}

export function parseConsigneeConsignmentDetailResponse(response?: ConsignmentDetailResponse | null): ConsigneeFormValues {
  return {
    typeOfPerson: response?.typeOfPerson ?? '',
    address: parseAddressResponse(response?.address),
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    id: response?.id ?? null,
    identificationNumber: response?.identificationNumber ?? '',
    name: response?.name ?? '',
    target: response ? TraderTargetEnum.parse(response?.target) : 'CONSIGNEE',
    deleted: false,
  }
}

export function parseConsignorConsignmentDetailResponse(response?: ConsignmentDetailResponse | null): ConsignorFormValues {
  return {
    typeOfPerson: response?.typeOfPerson ?? '',
    address: parseAddressResponse(response?.address),
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    id: response?.id ?? null,
    identificationNumber: response?.identificationNumber ?? '',
    name: response?.name ?? '',
    target: response ? TraderTargetEnum.parse(response?.target) : 'CONSIGNOR',
    deleted: false,
  }
}

export function parseCarrierConsignmentDetailResponse(response?: ConsignmentDetailResponse | null): CarrierFormValues {
  return {
    address: parseAddressResponse(response?.address),
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    id: response?.id ?? null,
    identificationNumber: response?.identificationNumber ?? '',
    name: response?.name ?? '',
    target: response ? TraderTargetEnum.parse(response?.target) : 'CARRIER',
    deleted: false,
  }
}

export function parseSellerConsignmentDetailResponse(response?: ConsignmentDetailResponse | null): SellerFormValues {
  return {
    typeOfPerson: response?.typeOfPerson ?? '',
    address: parseAddressResponse(response?.address),
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    id: response?.id ?? null,
    identificationNumber: response?.identificationNumber ?? '',
    name: response?.name ?? '',
    target: response ? TraderTargetEnum.parse(response?.target) : 'SELLER',
    deleted: false,
  }
}

export function parseBuyerConsignmentDetailResponse(response?: ConsignmentDetailResponse | null): BuyerFormValues {
  return {
    typeOfPerson: response?.typeOfPerson ?? '',
    address: parseAddressResponse(response?.address),
    contactPerson: {
      deleted: false,
      email: response?.contactPerson?.email ?? '',
      id: response?.contactPerson?.id ?? null,
      name: response?.contactPerson?.name ?? '',
      telephone: response?.contactPerson?.telephone ?? '',
    },
    id: response?.id ?? null,
    identificationNumber: response?.identificationNumber ?? '',
    name: response?.name ?? '',
    target: response ? TraderTargetEnum.parse(response?.target) : 'BUYER',
    deleted: false,
  }
}
